// motion-alpine.js
// Alpine plugin that integrates the motion choreography runtime with Alpine state.
//
// Provides:
//   x-motion="modal-enter"                 — declarative motion binding
//   x-motion:open / x-motion:close         — explicit triggers
//   x-motion.show="state"                  — runs enter when state is truthy, exit when falsy
//   $motion.run('list-reveal', el)         — magic for imperative use inside x-data
//
// Drop into your existing Alpine bootstrap:
//   import Alpine from 'alpinejs';
//   import motionPlugin from './motion-alpine.js';
//   import { run, choreography } from './motion-choreography.js';
//   Alpine.plugin(motionPlugin({ run, choreography }));
//   Alpine.start();

export default function motionPlugin({ run, choreography }) {
  return (Alpine) => {

    // -------------------------------------------------------------------
    // $motion — imperative magic, usable inside any x-data scope.
    // Example: <button @click="$motion.run('list-reveal', $refs.list)">
    // -------------------------------------------------------------------
    Alpine.magic('motion', () => ({
      run(name, el) { return run(`choreography-${name}`, el); },
      get tokens() { return choreography; }
    }));

    // -------------------------------------------------------------------
    // x-motion — declarative binding on the element it lives on.
    // The token name is the directive value:  x-motion="modal-enter"
    //
    // Modifiers control when it fires:
    //   :open      — element marked as an opener; fires on click within scope
    //   :close     — same, for closers
    //   .show="x"  — observes truthy/falsy and runs enter/exit accordingly
    //
    // With no modifier, the choreography runs once on mount (useful for
    // list-reveal and similar entrance-on-mount cases).
    // -------------------------------------------------------------------
    Alpine.directive('motion', (el, { value, expression, modifiers }, { evaluateLater, effect, cleanup }) => {
      const tokenName = (expression || '').trim();
      if (!tokenName) {
        console.warn('[x-motion] missing token name on', el);
        return;
      }

      // Convention: "modal-enter" implies a partner "modal-exit" for .show.
      const pairedExit = tokenName.endsWith('-enter')
        ? tokenName.replace(/-enter$/, '-exit')
        : null;

      // --- :open / :close trigger handlers ---
      if (value === 'open' || value === 'close') {
        const handler = () => {
          // Find the nearest [x-motion] root above us — that's our scope.
          const root = el.closest('[x-motion]') || el.parentElement;
          const fireToken = value === 'open' ? tokenName : (pairedExit || tokenName);
          run(`choreography-${fireToken}`, root);
        };
        el.addEventListener('click', handler);
        cleanup(() => el.removeEventListener('click', handler));
        return;
      }

      // --- .show — react to a boolean expression ---
      if (modifiers.includes('show')) {
        const getter = evaluateLater(expression);
        let lastValue = undefined;
        let initialized = false;
        effect(() => {
          getter(value => {
            const isOpen = !!value;
            // Skip the first run if we're already in the "natural" state.
            if (!initialized) { lastValue = isOpen; initialized = true; return; }
            if (isOpen === lastValue) return;
            lastValue = isOpen;
            const token = isOpen ? tokenName : (pairedExit || tokenName);
            run(`choreography-${token}`, el);
          });
        });
        return;
      }

      // --- default: run once on mount ---
      requestAnimationFrame(() => run(`choreography-${tokenName}`, el));
    });
  };
}
